import router from '@/router'

/**
 * Resolves route configuration to be compatible between Vue Router v3 (Vue 2.x) and v4 (Vue 3.x)
 * @typedef {import('vue-router').RouteLocation} RouteLocation
 * @typedef {import('vue-router').Router} VueRouter
 * @typedef {import('vue-router').RouteRecordRaw} ResolvedRoute
 * @typedef {Object} RouterOptions
 * @property {VueRouter} router - Custom router instance
 *
 * @param {RouteLocation} route - Route configuration object
 * @param {RouteLocation} currentLocation - Current location object
 * @param {RouterOptions} options - Options object
 * @returns {ResolvedRoute} Route object
 */
export const resolveRoute = (route, currentLocation, { router: customRouter, try: tryResolve = true } = {}) => {
  const routerToUse = customRouter || router
  try {
    return routerToUse.resolve(route, currentLocation)
  } catch (error) {
    const routes = routerToUse.getRoutes()
    if (route.name) {
      const possibleRoute = routes.find((r) => r.name === route.name)
      if (possibleRoute) {
        return possibleRoute
      }
    }
    if (tryResolve) {
      return null
    }
    throw error
  }
}

/**
 * Returns the appropriate organization redirect route.
 *
 * Resolves the given `appSection` into a route using `resolveRoute`. Falls back to the
 * `dashboard` route when the resolved route is not scoped to `orgCanonical` (e.g. the root
 * path or the org-agnostic `/organizations` picker), so switching organizations always lands
 * the user inside the newly selected organization.
 *
 * @function
 * @param {Object} options - Options object
 * @param {string} options.appSection - The application section name (e.g., `'projects'`, `'dashboard'`)
 * @param {string} options.orgCanonical - The canonical of the organization to scope the route to
 * @returns {import('vue-router').Route} A Vue Router route object to redirect to
 */
export const resolveAppSectionRoute = ({ appSection, orgCanonical }) => {
  const route = resolveRoute({ name: appSection, params: { orgCanonical } })
  const isOrgScopedRoute = route?.path?.includes(`/organizations/${orgCanonical}`)
  if (!isOrgScopedRoute) {
    return { name: 'dashboard', params: { orgCanonical } }
  }
  return route
}
